/**
 * CampaignLimitReached
 *
 * Displays upgrade-or-delete guidance when the user hits campaign limits,
 * with plan info and actions to manage campaigns or upgrade.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, FolderOpen, Crown, Trash2, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardFooter, CardHeader } from '../../ui/layout/card';

export default function CampaignLimitReached({ userPlan, campaignsCount, planLimit }) {
  const navigate = useNavigate();
  
  // Determine if user can upgrade based on their plan
  const canUpgrade = userPlan !== 'premium';
  
  // Get plan name for display
  const getPlanDisplayName = (plan) => {
    switch (plan) {
      case 'basic':
        return 'Standard';
      case 'premium':
        return 'Premium';
      default:
        return plan;
    }
  };
  
  // Get next tier plan name
  const getNextTierPlan = () => {
    return 'Premium';
  };
  
  // Get next tier limit
  const getNextTierLimit = () => {
    return 25;
  };

  return (
    <Card className="w-full border border-gray-200 dark:border-gray-800 shadow-sm">
      <CardHeader className="pb-3">
        <div className="rounded-lg border border-primary-200 bg-primary-50 p-4 dark:border-primary-800/60 dark:bg-primary-900/20">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex-shrink-0">
              <AlertTriangle className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white">
                Campaign Limit: {campaignsCount}/{planLimit}
              </h3>
              <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                You need to either delete existing campaigns or upgrade your plan to create new ones.
              </p>
            </div>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Your Options</h3>
          
          <div className="grid gap-4 md:grid-cols-2">
            <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700 transition-colors bg-white dark:bg-gray-900">
              <div className="flex items-start gap-3">
                <div className="mt-1 flex-shrink-0 rounded-full bg-red-100 p-1.5 dark:bg-red-900/30">
                  <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">Delete Existing Campaigns</h4>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    You can delete campaigns you no longer need to make room for new ones.
                  </p>
                </div>
              </div>
            </div>
            
            {canUpgrade && (
              <div className="p-4 rounded-lg border border-primary-200 bg-primary-50 dark:border-primary-800/60 dark:bg-primary-900/20">
                <div className="flex items-start gap-3">
                  <div className="mt-1 flex-shrink-0 rounded-full bg-primary-100 p-1.5 dark:bg-primary-900/50">
                    <Crown className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white">Upgrade Your Plan</h4>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      Upgrade to {getNextTierPlan()} to create up to {getNextTierLimit()} campaigns and unlock more features.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Plan Comparison</h3>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">Basic Plan:</span>
              <span className="font-medium text-gray-900 dark:text-white">1 campaign</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">Premium Plan:</span>
              <span className="font-medium text-gray-900 dark:text-white">25 campaigns</span>
            </div>
          </div>
        </div>
      </CardContent>
      
      <CardFooter className="flex justify-between border-t border-gray-200 dark:border-gray-800 pt-4">
        <div></div>
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <button
            type="button"
            onClick={() => navigate('/app/campaigns')}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            <FolderOpen className="h-4 w-4" />
            Manage Campaigns
          </button>
          
          {canUpgrade && (
            <button
              type="button"
              onClick={() => navigate('/app/account', { state: { fromCreateCampaign: true } })}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-primary-700 dark:bg-primary-700 dark:hover:bg-primary-600"
            >
              <Crown className="h-4 w-4" />
              Upgrade Plan
              <ArrowRight className="h-4 w-4 ml-1" />
            </button>
          )}
        </div>
      </CardFooter>
    </Card>
  );
} 